import React, { useState, useEffect } from "react";
import { Box, Layer, Text, Button, Spinner } from "grommet";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { QuizHeader } from "../../components/QuizComponents/QuizHeader";
import { QuizContent } from "../../components/QuizComponents/QuizContent";
import { QuizContentWithColoredBoxes } from "../../components/QuizComponents/SampleQuizContent/QuizContentWithColoredOptions";
import axios from "../../config/adminAxios";
import API_URL from "../../config/config";
import { useQuizContext } from "../../contexts/QuizContext";

const QuizPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user_id, assessment_id } = useParams();
  const [questions, setQuestions] = useState([{}]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null); // New state
  const [showAlert, setShowAlert] = useState(false);
  const [stopAlert, setStopAlert] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [numberOfQuestions, setNumberOfQuestions] = useState();
  const [noAnswerQuestionIds, setNoAnswerQuestionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const { resetQuizAttempt } = useQuizContext();
  const subProductSkillIds = location.state?.subProductSkillIds || [];

  // Function to format the date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    return `${year}-${month}-${day}`;
  };

  // Function to format the time as HH:MM:SS
  const formatStartTime = (date) => {
    const hours = `0${date.getHours()}`.slice(-2);
    const minutes = `0${date.getMinutes()}`.slice(-2);
    const seconds = `0${date.getSeconds()}`.slice(-2);
    return `${hours}:${minutes}:${seconds}`;
  };

  // Fetch questions from the backend
  const fetchQuestions = async () => {
    try {
      
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/quiz/questions/${assessment_id}`,
        { subProductSkillIds },
      );
      if (response) {
        const fetchedQuestions = response.data.selectedQuestions;
        
        // Shuffle fetchedQuestions using the Fisher-Yates algorithm
        for (let i = fetchedQuestions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [fetchedQuestions[i], fetchedQuestions[j]] = [
            fetchedQuestions[j],
            fetchedQuestions[i],
          ];
        }

        

        setQuestions(fetchedQuestions);
        sessionStorage.setItem("currentAssessmentId", assessment_id);
      } else {
        alert("Failed to fetch questions from the server.");
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
    const savedProgress =
      JSON.parse(sessionStorage.getItem("localQuizProgress")) || {};
    const storedAssessmentId = sessionStorage.getItem("currentAssessmentId");

    // Check if stored assessment_id matches the current assessment_id
    if (storedAssessmentId === assessment_id) {
      const initialNumberOfQuestions =
        savedProgress.numberOfQuestions || location.state?.query_count;

      setNumberOfQuestions(initialNumberOfQuestions);

      // Restore saved progress if any
      setCurrentQuestionIndex(savedProgress.currentQuestionIndex || 0);
      setTimeLeft(savedProgress.timeLeft || initialNumberOfQuestions * 60);
      setIsPaused(savedProgress.isPaused || false);
      setSelectedOption(savedProgress.selectedOption || null);
      setAnswers(savedProgress.answers || []);
      setNoAnswerQuestionIds(savedProgress.noAnswerQuestionIds || []);
    } else {
      // Reset if assessment_id does not match
      setNumberOfQuestions(location.state?.query_count);
      setCurrentQuestionIndex(0);
      setTimeLeft(location.state?.query_count * 60);
      setIsPaused(false);
      setSelectedOption(null);
      setAnswers([]);
      setNoAnswerQuestionIds([]);
    }
  }, [location.state]);

  // Save progress to sessionStorage whenever quiz state changes
  useEffect(() => {
    if (currentQuestionIndex !== null) {
      sessionStorage.setItem(
        "localQuizProgress",
        JSON.stringify({
          currentQuestionIndex,
          timeLeft,
          selectedOption,
          answers,
          isPaused,
          numberOfQuestions,
          noAnswerQuestionIds,
        }),
      );
    }
  }, [currentQuestionIndex, timeLeft, isPaused, selectedOption, answers, numberOfQuestions, noAnswerQuestionIds]);

  // Dedicated timer effect — only depends on timeLeft, isPaused, loading, submitLoading
  useEffect(() => {
    if (loading || timeLeft === null || isPaused || timeLeft <= 0 || submitLoading) return;
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, isPaused, loading, submitLoading]);

  useEffect(() => {
    if (timeLeft === 0) {
      handleTimeUp();
    }
  }, [timeLeft]);
  const saveCurrentAnswer = () => {
    const questionId = questions[currentQuestionIndex].id;
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: {
        selectedOption,
        selectedOptionIndex,
      },
    }));

    // Update noAnswerQuestionIds accordingly
    if (selectedOption === "No") {
      setNoAnswerQuestionIds((prevIds) => {
        if (!prevIds.includes(questionId)) {
          return [...prevIds, questionId];
        } else {
          return prevIds;
        }
      });
    } else {
      setNoAnswerQuestionIds((prevIds) =>
        prevIds.filter((id) => id !== questionId),
      );
    }
  };
  const handleNextQuestion = () => {
    
    if (!selectedOption) {
      setShowAlert(true);
    } else {
      saveCurrentAnswer();

      if (currentQuestionIndex === numberOfQuestions - 1) {
        // Directly submit the quiz
        submitQuiz();
      } else {
        proceedToNextQuestion();
      }
    }
  };
  const handlePreviousQuestion = () => {
    
    saveCurrentAnswer();
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
    }
  };
  const proceedToNextQuestion = () => {
    
    setSelectedOption(null);
    setSelectedOptionIndex(null);
    setShowAlert(false);

    if (currentQuestionIndex < numberOfQuestions - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    } else {
      submitQuiz();
    }
  };

  useEffect(() => {
    const questionId = questions[currentQuestionIndex].id;
    const previousAnswer = answers[questionId];
    if (previousAnswer) {
      setSelectedOption(previousAnswer.selectedOption);
      setSelectedOptionIndex(previousAnswer.selectedOptionIndex);
    } else {
      setSelectedOption(null);
      setSelectedOptionIndex(null);
    }
  }, [currentQuestionIndex]);

  // Define the handleTimeUp function
  const handleTimeUp = () => {
    let updatedAnswers = [...answers];
    let updatedNoAnswerQuestionIds = [...noAnswerQuestionIds];

    if (selectedOption) {
      updatedAnswers.push({
        question: questions[currentQuestionIndex].question,
        selectedOption,
      });

      if (selectedOption === "No") {
        updatedNoAnswerQuestionIds.push(questions[currentQuestionIndex].id);
      }
    }

    // Update the state with the latest data
    setAnswers(updatedAnswers);
    setNoAnswerQuestionIds(updatedNoAnswerQuestionIds);

    sessionStorage.setItem(
      "noAnswerQuestionIds",
      JSON.stringify(updatedNoAnswerQuestionIds),
    );

    // Now call submitQuiz
    submitQuiz();
  };

  const submitQuiz = async () => {
    setSubmitLoading(true);
    try {
      const now = new Date();
      const currentDate = formatDate(now);
      const currentTime = formatStartTime(now);

      
      if (selectedOption === "No") {
        const lastQuestionId = questions[currentQuestionIndex]?.id;
        setNoAnswerQuestionIds((prevIds) => {
          const updatedIds = [...prevIds, lastQuestionId];
          sessionStorage.setItem(
            "noAnswerQuestionIds",
            JSON.stringify(updatedIds),
          );
          return updatedIds;
        });
      }
      // Send noAnswerQuestionIds to backend
      const response = await axios.post(`${API_URL}/dashboard/loadlp`, {
        question_ids: [
          ...noAnswerQuestionIds,
          selectedOption === "No" ? questions[currentQuestionIndex].id : null,
        ].filter((id) => id !== null),
        assessment_id: assessment_id,
        user_id: user_id,
        date: currentDate,
        time: currentTime,
      });

      if (response) {
        
        resetQuizAttempt();
        setSubmitLoading(false);
        navigate(`/dashboard/${user_id}`, { state: { assessment_id } });
      } else {
        setSubmitLoading(false);
        alert("There was an error submitting your quiz.");
      }
    } catch (error) {
      setSubmitLoading(false);
      console.error("Error submitting quiz:", error);
      alert("There was an error submitting your quiz.");
    }
  };

  const handleAlertContinue = () => {
    proceedToNextQuestion("No");
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  const handleStopAlert = () => {
    setStopAlert(true);
  };

  const handleStop = () => {
    resetQuizAttempt();
    sessionStorage.removeItem("currentAssessmentId");
    sessionStorage.removeItem("localQuizProgress");
    sessionStorage.removeItem("noAnswerQuestionIds");
    navigate(`/add-proficiency/${user_id}`);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  if (loading) {
    return (
      <Box fill align="center" justify="center">
        <Spinner size="large" />
        <Text>Loading questions...</Text>
      </Box>
    );
  }

  return (
    <Box
      fill="vertical"
      justify="between"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onSelect={(e) => e.preventDefault()}
    >
      <Box flex="grow">
        <HeaderNavigation quizStarted={true} />
        <QuizHeader
          currentQuestionIndex={currentQuestionIndex}
          numberOfQuestions={numberOfQuestions}
          isPaused={isPaused}
          timeLeft={timeLeft}
          handlePause={handlePause}
          handleStop={handleStopAlert}
          formatTime={formatTime}
        />

        {/* Conditionally render the appropriate component based on question weight */}
        {questions[currentQuestionIndex]?.weight === 2 ? (
          <QuizContentWithColoredBoxes
            questions={questions}
            currentQuestionIndex={currentQuestionIndex}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            selectedOptionIndex={selectedOptionIndex} // Pass down
            setSelectedOptionIndex={setSelectedOptionIndex} // Pass down
            handleNextQuestion={handleNextQuestion}
            handlePreviousQuestion={handlePreviousQuestion} // Pass down
            isPaused={isPaused}
            isLastQuestion={currentQuestionIndex === numberOfQuestions - 1}
            isFirstQuestion={currentQuestionIndex === 0} // For disabling back button
          />
        ) : (
          <QuizContent
            questions={questions}
            currentQuestionIndex={currentQuestionIndex}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            handleNextQuestion={handleNextQuestion}
            handlePreviousQuestion={handlePreviousQuestion} // Pass down
            isPaused={isPaused}
            isLastQuestion={currentQuestionIndex === numberOfQuestions - 1}
            isFirstQuestion={currentQuestionIndex === 0} // For disabling back button
          />
        )}
      </Box>
      <StickyFooter />

      {showAlert && (
        <Layer
          onEsc={() => setShowAlert(false)}
          onClickOutside={() => setShowAlert(false)}
        >
          <Box pad="medium" gap="small">
            <Text>You have not attempted this question.</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Continue" onClick={handleAlertContinue} />
              <Button label="Cancel" onClick={() => setShowAlert(false)} />
            </Box>
          </Box>
        </Layer>
      )}
      {stopAlert && (
        <Layer
          onEsc={() => setStopAlert(false)}
          onClickOutside={() => setStopAlert(false)}
        >
          <Box pad="medium" gap="small">
            <Text>Are you sure you want to stop the assessment?</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Yes, sure" color="warn" onClick={handleStop} />
              <Button label="Cancel" onClick={() => setStopAlert(false)} />
            </Box>
          </Box>
        </Layer>
      )}
      {submitLoading && (
        <Layer>
          <Box
            pad="medium"
            gap="small"
            align="center"
            alignContent="center"
            alignSelf="center"
          >
            <Text>Submitting your assessment...</Text>
            <Spinner />
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default QuizPage;
